import { useMemo, useEffect, useState, useRef, useCallback } from 'react';
import {
  Link,
  useLocation,
  useSearchParams,
  useNavigate,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Fab,
  Box,
  Select,
  Tooltip,
  MenuItem,
  FormControl,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import OverLayLoader from '../components/OverLayLoader';
import NowrapWrapper from '../components/NowrapWrapper';
import FunctionalTable from '../components/FunctionalTable';
import { SelectColumnFilter } from '../components/ColumnFilter';
import { Keywords, EditableCell, KeyNames } from '../components/EditableCell';
import DictionaryFilter from '../components/DictionaryFilter';
import FloatingActionButtonHolder from '../components/FloatingActionButtonHolder';
import useDeleteRow from '../hooks/useDeleteRow';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import DeleteRowButton from '../components/DeleteRowButton';

import {
  getCodeList,
  getLanguages,
  updateCode,
  downloadCode,
  getCoding,
  deleteProfileCode,
} from '../services';
import useTitle from '../hooks/useTitle';
import usePersistColumns from '../hooks/usePersistColumns';
import { CONSTANT_KEYS } from '../helpers/tableKeys';
import {
  languageOption,
  optionalServiceColumns,
  ageOptions,
  CODING_GROUP,
} from '../helpers/options';
import { codeList } from '../utils/filters';
import saveFile from '../utils/saveFile';
import { createErrorNotification } from '../utils/createErrorNotification';
import { useAuth } from '../context/auth/AuthContext';

const {
  AGE,
  CODE,
  TEETH,
  NAME_RU,
  NAME_EN,
  WORDS_RU,
  WORDS_EN,
  PROFILE,
  PROFILES,
  LANGUAGE_NAME,
  NAMES,
  WORDS,
  COMMENT,
  PROFILE_NAME,
} = CONSTANT_KEYS;
const { OFFSET, LIMIT, NAME, LANGUAGE_ID, CODING } = codeList;

const initialFilter = {
  language: languageOption,
  additions: [WORDS, PROFILE],
};

const ServicesView = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [codeData, setCodeData] = useState([]);
  const [filter, setFilter] = useState(initialFilter);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [profiles, setProfiles] = useState([]);
  const [coding, setCoding] = useState([]);
  const [isOptionsLoading, setIsOptionsLoading] = useState(true);
  const [isSearchParamsLoading, setIsSearchParamsLoading] = useState(true);
  const [fetchKey, setFetchKey] = useState(0);

  const {
    isDeleting,
    itemToDelete: codeToDelete,
    openDeleteDialog,
    closeDeleteDialog,
    confirmDelete,
  } = useDeleteRow({
    onDelete: item => deleteProfileCode(item.id),
    onSuccess: () => setFetchKey(prev => prev + 1),
  });

  const languageList = useRef([]);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { t } = useTranslation(['services', 'promptbar', 'common']);

  const title = t('common:pages.services.primary');
  const pageTitle = t('promptbar:pageTitle', {
    title,
  });

  useTitle(pageTitle);

  usePersistColumns('servicesColumns', ['language', 'additions', 'profiles']);

  useEffect(() => {
    if (isOptionsLoading || !user) {
      return;
    }

    if (searchParams.has('language') && searchParams.get('language')?.length) {
      const languages = searchParams.get('language').split(',');
      setFilter(prev => ({ ...prev, language: languages }));
    } else {
      searchParams.set('language', initialFilter.language);
      setFilter(prev => ({ ...prev, language: initialFilter.language }));
    }

    if (
      searchParams.has('additions') &&
      !searchParams.get('additions')?.length
    ) {
      setFilter(prev => ({ ...prev, additions: [] }));
    } else if (
      searchParams.has('additions') &&
      searchParams.get('additions')?.length
    ) {
      const additions = searchParams.get('additions').split(',');
      setFilter(prev => ({ ...prev, additions }));
    } else {
      searchParams.set('additions', initialFilter.additions.join(','));
      setFilter(prev => ({ ...prev, additions: initialFilter.additions }));
    }

    if (
      !searchParams.has('profiles') ||
      !searchParams.get('profiles')?.length
    ) {
      const defaultProfiles = user.profiles
        .filter(pr => pr.is_default)
        .map(el => el.profile_name);
      if (defaultProfiles.length) {
        searchParams.set(PROFILES, defaultProfiles.join(','));
      }
    }

    if (!searchParams.has('coding') || !searchParams.get('coding')?.length) {
      const codingStr = coding.map(c => c.name).join(',');
      searchParams.set(CODING, codingStr);
    }

    setSearchParams(searchParams, { replace: true });
    setIsSearchParamsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOptionsLoading, coding]);

  useEffect(() => {
    if (!user) {
      return;
    }

    const fetchOptions = async () => {
      try {
        const [langs, allCoding] = await Promise.all([
          getLanguages(),
          getCoding(),
        ]);
        setCoding(
          allCoding.data.filter(c => c.group === CODING_GROUP.SERVICES),
        );
        setProfiles(user.profiles);
        languageList.current = langs.data;
      } catch (error) {
        console.log(error.message);
      } finally {
        setIsOptionsLoading(false);
      }
    };
    fetchOptions();
  }, [user]);

  const fetchData = useCallback(async ({ params, signal }) => {
    setIsLoading(true);

    const newParams = {
      [OFFSET]: params[OFFSET],
      [LIMIT]: params[LIMIT],
      ...(params[CODE]
        ? {
            codes: params[CODE].replace(/[;, ]+/g, ';').replace(/[;\s]*$/, ''),
          }
        : {}),
      ...(params[NAME_RU]
        ? {
            rus_name: params[NAME_RU],
          }
        : {}),
      ...(params[NAME_EN]
        ? {
            eng_name: params[NAME_EN],
          }
        : {}),
      ...(params[WORDS_RU]
        ? {
            ru_words_count: params[WORDS_RU],
          }
        : {}),
      ...(params[WORDS_EN]
        ? {
            en_words_count: params[WORDS_EN],
          }
        : {}),
      ...(params[PROFILES] && params[PROFILES]?.length > 0
        ? Array.isArray(params[PROFILES])
          ? { profile_name: params[PROFILES] }
          : { profile_name: params[PROFILES]?.split(',') }
        : {}),
      ...(params[AGE]
        ? {
            age: params[AGE],
          }
        : {}),
      ...(params[TEETH]
        ? {
            teeth: params[TEETH],
          }
        : {}),
      ...(params[COMMENT]
        ? {
            comment: params[COMMENT],
          }
        : {}),
      ...(params[CODING] && params[CODING]?.length > 0
        ? Array.isArray(params[CODING])
          ? { coding: params[CODING] }
          : { coding: params[CODING].split(',') }
        : {}),
    };

    try {
      const data = await getCodeList(newParams, signal);
      if (data) {
        const newData = data.data.map(el => ({ ...el, uniqId: el.id }));
        setCodeData(prev =>
          params[OFFSET] === 0 ? [...newData] : [...prev, ...newData],
        );
        setHasMoreData(prev => (params[OFFSET] === 0 ? true : prev));
        if (data.data.length < params[LIMIT]) {
          setHasMoreData(false);
        }
        setIsLoading(false);
      }
    } catch (error) {
      console.log(error.message);
      setIsLoading(false);
    }
  }, []);

  const handleFilterReset = () => {
    navigate(location.pathname, { replace: true });
    setTimeout(() => navigate(0));
  };

  const handleFilterSelect = event => {
    const { value, name } = event.target;
    searchParams.set(name, value);
    setSearchParams(searchParams);
    setFilter(prev => ({ ...prev, [name]: value }));
  };

  const handleChangeCodeData = ({ index, value, columnName, isValid }) => {
    switch (columnName) {
      case NAME_RU:
      case NAME_EN: {
        const lang = columnName.split('_')[1];
        setCodeData(prev =>
          prev.map(row => {
            if (index === row.id) {
              const nameIndex = row.names.findIndex(
                name => name[LANGUAGE_NAME] === lang,
              );
              if (nameIndex === -1) {
                const currentLanguageId = languageList.current.find(
                  l => l.name === lang,
                );
                return {
                  ...row,
                  isValid: row.isValid
                    ? { ...row.isValid, [columnName]: isValid }
                    : { [columnName]: isValid },
                  [NAMES]: [
                    ...row.names,
                    {
                      [NAME]: value,
                      [LANGUAGE_ID]: currentLanguageId.id,
                      [LANGUAGE_NAME]: currentLanguageId.name,
                    },
                  ],
                };
              } else {
                return {
                  ...row,
                  isValid: row.isValid
                    ? { ...row.isValid, [columnName]: isValid }
                    : { [columnName]: isValid },
                  [NAMES]: row.names.map(el =>
                    el[LANGUAGE_NAME] === lang ? { ...el, name: value } : el,
                  ),
                };
              }
            } else {
              return row;
            }
          }),
        );
        break;
      }

      case TEETH: {
        setCodeData(prev =>
          prev.map(row =>
            index === row.id
              ? {
                  ...row,
                  [TEETH]: value,
                  isValid: row.isValid
                    ? { ...row.isValid, [columnName]: isValid }
                    : { [columnName]: isValid },
                }
              : row,
          ),
        );
        break;
      }

      case AGE: {
        setCodeData(prev =>
          prev.map(row =>
            index === row.id
              ? {
                  ...row,
                  [AGE]: value,
                }
              : row,
          ),
        );
        break;
      }

      case COMMENT: {
        setCodeData(prev =>
          prev.map(row =>
            index === row.id
              ? {
                  ...row,
                  [COMMENT]: value,
                }
              : row,
          ),
        );
        break;
      }

      default:
        return;
    }
  };

  const editHandler = codeId => {
    setCodeData(prev =>
      prev.map(el =>
        el.id === codeId ? { ...el, isEdit: true, prevState: el } : el,
      ),
    );
  };

  const applyChangesHandler = async (codeId, data) => {
    if (data.isValid && typeof data.isValid === 'object') {
      const isDataValid = Object.values(data.isValid).every(el => el);
      if (!isDataValid) return;
    }

    try {
      setIsLoading(true);
      const newData = {
        code: data.code,
        age: data.age,
        teeth: data.teeth,
        comment: data.comment,
        names: data.names,
        coding: data.coding,
        coding_group: coding[0].group,
      };

      const result = await updateCode(codeId, newData);
      if (result) {
        // setCodeData(prev =>
        //   prev.map(el => {
        //     if (el.id === codeId) {
        //       delete el.prevState;
        //       delete el.isEdit;
        //       return el;
        //     } else {
        //       return el;
        //     }
        //   }),
        // );
        window.location.reload();
      }
    } catch (error) {
      if (error?.response?.status === 422 && error?.response?.data?.detail) {
        createErrorNotification(error.response.data.detail);
      } else {
        createErrorNotification();
      }
      console.log(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelChangesHandler = codeId => {
    setCodeData(prev =>
      prev.map(el => (el.id === codeId ? { ...el.prevState } : el)),
    );
  };

  const handleDownloadExcel = async () => {
    try {
      setIsLoading(true);

      const profiles = searchParams.get('profiles')?.split(',');
      const coding = searchParams.get('coding')?.split(',');
      const params = {
        [PROFILE_NAME]: profiles,
        [CODING]: coding,
      };

      const result = await downloadCode(params);

      if (result) {
        const blob = new Blob([result.data], {
          type: 'application/octet-stream',
        });
        saveFile(blob, 'codes.xlsx');
      }
    } catch (error) {
      createErrorNotification();
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteActionComponent = useCallback(
    row => {
      const isBasicProfile = row.profiles?.some(prof => prof.technical);

      // в строках профиля basic кнопка удаления не отображается
      if (row.isEdit || isBasicProfile || isDeleting) {
        return null;
      }

      const profileName = row.profiles?.map(item => item.name).join('; ');
      const codeName =
        row.names?.find(el => el[LANGUAGE_NAME] === 'ru')?.name ||
        row.names?.[0]?.name;

      return (
        <DeleteRowButton
          disabled={isDeleting}
          onClick={() =>
            openDeleteDialog({
              id: row.uniqId,
              code: row.code,
              codeName,
              profileName,
            })
          }
        />
      );
    },
    [isDeleting, openDeleteDialog],
  );

  const columns = useMemo(
    () => [
      {
        Header: <NowrapWrapper title={t(`table.service_code`)} />,
        accessor: CODE,
        id: CODE,
        style: { width: '6%', maxWidth: '100px', minWidth: '100px' },
      },
      {
        Header: <NowrapWrapper title={t(`table.name_ru`)} />,
        accessor: NAMES,
        id: NAME_RU,
        show: filter.language.includes('ru'),
        style: { width: '17%' },
        Cell: ({ value, ...cell }) => {
          const isEdit = cell.row.original.isEdit;
          const data = value.find(
            el => el[LANGUAGE_NAME] === languageOption[0],
          );

          return (
            <Box
              sx={{
                width: '100%',
              }}
            >
              {!isEdit ? (
                <KeyNames data={data} />
              ) : (
                <EditableCell
                  value={data?.name ?? ''}
                  cell={cell}
                  onChangeData={handleChangeCodeData}
                  pathname={location.pathname}
                />
              )}
            </Box>
          );
        },
      },
      {
        Header: <NowrapWrapper title={t(`table.name_en`)} />,
        accessor: NAMES,
        id: NAME_EN,
        show: filter.language.includes('en'),
        style: { width: '17%' },
        Cell: ({ value, ...cell }) => {
          const isEdit = cell.row.original.isEdit;
          const data = value.find(
            el => el[LANGUAGE_NAME] === languageOption[1],
          );

          return (
            <Box
              sx={{
                width: '100%',
              }}
            >
              {!isEdit ? (
                <KeyNames data={data} />
              ) : (
                <EditableCell
                  value={data?.name ?? ''}
                  cell={cell}
                  onChangeData={handleChangeCodeData}
                  pathname={location.pathname}
                />
              )}
            </Box>
          );
        },
      },
      {
        Header: <NowrapWrapper title={t(`table.comment`)} />,
        accessor: COMMENT,
        id: COMMENT,
        show: filter.additions.includes(COMMENT),
        style: { width: '16%' },
        Cell: ({ value, ...cell }) => {
          const isBasicProfile = cell.row.original.profiles.find(
            prof => prof.technical,
          );
          const isEdit = cell.row.original.isEdit && isBasicProfile;

          return (
            <Box
              sx={{
                width: '100%',
              }}
            >
              {!isEdit ? (
                value
              ) : (
                <EditableCell
                  value={value}
                  cell={cell}
                  onChangeData={handleChangeCodeData}
                  pathname={location.pathname}
                />
              )}
            </Box>
          );
        },
      },
      {
        Header: <NowrapWrapper title={t(`table.words_ru`)} />,
        id: WORDS_RU,
        accessor: WORDS,
        show:
          filter.additions.includes(CONSTANT_KEYS.WORDS) &&
          filter.language.includes('ru'),
        style: { width: '12%' },
        Cell: Keywords,
      },
      {
        Header: <NowrapWrapper title={t(`table.words_en`)} />,
        id: WORDS_EN,
        accessor: WORDS,
        show:
          filter.additions.includes(CONSTANT_KEYS.WORDS) &&
          filter.language.includes('en'),
        style: { width: '12%' },
        Cell: Keywords,
      },
      {
        Header: <NowrapWrapper title={t('table.coding')} />,
        id: CODING,
        accessor: CODING,
        show: filter.additions.includes(CODING),
        style: { width: '6%', maxWidth: '100px', minWidth: '100px' },
        Filter: cell => {
          const codingNames = coding.map(c => c.name);
          return (
            <SelectColumnFilter
              cell={cell}
              options={codingNames}
              multiple
              isValueRequired
            />
          );
        },
      },
      {
        Header: <NowrapWrapper title={t(`table.profile`)} />,
        id: PROFILES,
        accessor: PROFILES,
        show: filter.additions.includes(PROFILE),
        style: { width: '6%', maxWidth: '110px', minWidth: '110px' },
        Filter: cell => {
          const profilesNames = profiles.map(el => el.profile_name);
          return (
            <SelectColumnFilter
              cell={cell}
              options={profilesNames}
              multiple
              isValueRequired
            />
          );
        },

        Cell: ({ value }) => value.map(item => item.name).join('; '),
      },
      {
        Header: <NowrapWrapper title={t(`table.age`)} />,
        id: AGE,
        accessor: AGE,
        show: filter.additions.includes(AGE),
        style: { width: '6%', maxWidth: '85px', minWidth: '85px' },
        Filter: cell => <SelectColumnFilter cell={cell} options={ageOptions} />,
        Cell: ({
          value: initialValue,
          row: { index, original },
          column: { id: columnName },
        }) => {
          const [value, setValue] = useState('');
          const isBasicProfile = original.profiles.find(prof => prof.technical);

          useEffect(() => {
            setValue(initialValue);
          }, [initialValue]);

          const isEdit = original.isEdit && isBasicProfile;
          return (
            <>
              {!isEdit ? (
                initialValue
              ) : (
                <FormControl fullWidth variant="standard">
                  <Select
                    id={'Select-' + columnName + '-' + index}
                    value={value || ''}
                    input={<OutlinedInput />}
                    name={columnName + '-' + index}
                    IconComponent={KeyboardArrowDownIcon}
                    MenuProps={{ autoFocus: false, sx: { maxWidth: false } }}
                    onChange={e => {
                      const selected = e.target.value;

                      setValue(selected);
                      handleChangeCodeData({
                        index: original.uniqId,
                        value: selected,
                        columnName,
                      });
                    }}
                  >
                    {ageOptions.map(el => (
                      <MenuItem key={el} value={el} title={el}>
                        <ListItemText primary={el} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </>
          );
        },
      },
      {
        Header: <NowrapWrapper title={t(`table.${TEETH}`)} />,
        id: TEETH,
        accessor: TEETH,
        show: filter.additions.includes(TEETH),
        style: { width: '6%', maxWidth: '85px', minWidth: '85px' },
        Cell: ({ value, ...cell }) => {
          const isBasicProfile = cell.row.original.profiles.find(
            prof => prof.technical,
          );
          const isEdit = cell.row.original.isEdit && isBasicProfile;

          return (
            <Box
              sx={{
                width: '100%',
              }}
            >
              {!isEdit ? (
                value ? (
                  <span>{value}</span>
                ) : (
                  '-'
                )
              ) : (
                <EditableCell
                  value={value}
                  cell={cell}
                  onChangeData={handleChangeCodeData}
                  pathname={location.pathname}
                />
              )}
            </Box>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filter, t, location.pathname, profiles, coding],
  );

  return (
    <>
      {(isLoading || isOptionsLoading || isSearchParamsLoading) && (
        <OverLayLoader />
      )}

      <Box pt={3}>
        <DictionaryFilter
          filter={filter}
          onFilterReset={handleFilterReset}
          onFilterSelect={handleFilterSelect}
          options={optionalServiceColumns}
          onDownload={handleDownloadExcel}
        />

        {!isSearchParamsLoading && (
          <FunctionalTable
            key={fetchKey}
            type="sticky"
            columns={columns}
            data={codeData}
            onEdit={editHandler}
            fetchData={fetchData}
            applyChanges={applyChangesHandler}
            cancelChanges={cancelChangesHandler}
            hasMoreData={hasMoreData}
            isInfiniteScroll
            pageSize={50}
            actionColumnComponent={deleteActionComponent}
          />
        )}

        <FloatingActionButtonHolder>
          <Tooltip title={t('promptbar:addServiceCode')} arrow>
            <Link to="add" state={location}>
              <Fab color="primary" aria-label="add code">
                <AddIcon />
              </Fab>
            </Link>
          </Tooltip>
        </FloatingActionButtonHolder>
      </Box>

      <DeleteConfirmDialog
        open={Boolean(codeToDelete)}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
        title={t('promptbar:deleteProfileCodeDialog.title')}
        description={t('promptbar:deleteProfileCodeDialog.description', {
          code: codeToDelete?.code || '-',
          profile: codeToDelete?.profileName || '-',
        })}
      />
    </>
  );
};

export default ServicesView;
